import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type Me } from "@/lib/api";

const MeContext = createContext<{ me: Me | null; can: (permission: string) => boolean }>({
  me: null,
  can: () => false,
});

/**
 * Permissions come from /me rather than the token, so a role edited in another
 * tab takes effect on the next refetch instead of at the next login.
 */
export function MeProvider({ children }: { children: ReactNode }) {
  const { data } = useQuery({ queryKey: ["me"], queryFn: api.me, staleTime: 60_000 });
  const me = data ?? null;
  const can = (permission: string) => me?.permissions.includes(permission) ?? false;
  return <MeContext.Provider value={{ me, can }}>{children}</MeContext.Provider>;
}

export const useMe = () => useContext(MeContext);

/** Renders children when the actor holds the permission, `fallback` otherwise. */
export function Can({
  do: permission,
  children,
  fallback = null,
}: {
  do: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { can } = useMe();
  return <>{can(permission) ? children : fallback}</>;
}
